<template>
  <router-link
    v-slot="{
      href, navigate,
    }"
    :to="actualRouteTo"
    custom
  >
    <a
      :href="href"
      class="router-link-app__link rounded-corners"
      @click="navigate"
    >
      <button
        v-tooltip="{
          value: i18n.global.t(`api.AppId.${appId}`),
        }"
        type="button"
        tabindex="-1"
        class="router-link-app rounded-corners"
      >
        <div
          class="router-link-app__background rounded-corners"
          :style="{
            'background-color': appColor,
            'border-color': appColor,
          }"
        />
        <component
          :is="getAppIcon(appId)"
          class="router-link-app__icon"
        />
        <span class="router-link-app__label">&nbsp;</span>
      </button>
    </a>
  </router-link>
</template>

<script setup lang="ts">
import {
  i18n,
} from '@i18n';
import {
  computed,
} from 'vue';
import {
  RouteLocationRaw,
} from 'vue-router';

import {
  TGetAllAppsWithPermissionsResponse,
} from '@/generated/qmulus/axios/core.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  EAppId,
} from '@/types/app-type.ts';
import {
  appListMap,
  getAppIcon,
} from '@/types/app-util.ts';
import {
  appTileColorMap,
} from '@/utils/app-tile.ts';

const appStore = useAppStore();
appStore.loadAppList();

const props = defineProps<{
  appId: EAppId,
  /**
   * Optional overwrite of the route target. if not provided, the button will navigate
   * to the defaul page of the app.
   */
  routeTo?: RouteLocationRaw,
}>();

const appDetails = computed<TGetAllAppsWithPermissionsResponse[number] | undefined>(() => (appStore.appList?.find((app) => (app.appId
  // no idea how to solve this TS error:
  // this one is fun: it says "unsused @ts-expect-error"
  // but when you remove it it says "TS error" :D
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: Expression produces a union type that is too complex to represent.ts-plugin(2590)
  === props.appId
))));
const appColor = computed<string | undefined>(() => (appDetails.value ? appTileColorMap[appStore.getAppGroup(appDetails.value)] : undefined));
const actualRouteTo = computed<RouteLocationRaw>(() => props.routeTo || {
  name: appListMap[props.appId].route as any,
});
</script>

<style scoped lang="scss">
.rounded-corners {
  border-radius: var(--border-radius);
}

.router-link-app {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-secondary);
  padding: 0.5rem;
  width: 1.75rem;
  height: 1.75rem;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 1px solid;
    opacity: 0;
  }

  &:hover > .router-link-app__background {
    opacity: 0.075;
  }

  // The link takes the keyboard focus, so the button inside it is left out of the tab order.
  &__link {
    &:focus-visible {
      outline: 0 none;
    }

    &:focus-visible > .router-link-app {
      outline: var(--focus-ring-width) solid var(--focus-outline-color);
      outline-offset: var(--focus-ring-gap);
    }

    &:focus-visible .router-link-app__background {
      opacity: 0.075;
    }
  }

  &__icon {
    min-width: 16px;
    min-height: 16px;
    width: 16px;
    height: 16px;
  }

  &__label {
    visibility: hidden;
    width: 0;
    flex: 0 0 auto;
  }
}
</style>
